import {
  HandlerFunction,
  RequestHandler,
  Message,
  ResponseMessage,
} from '@/types'
import { BRIDGE_SOURCE, CODE_ENUM } from '@/constants'
import { v1 } from 'uuid'

// 默认依赖
export const defaultPayload = {
  code: CODE_ENUM.SUCCESS,
  message: '',
  data: '',
}

class Core {
  // 默认超时时间
  protected defaultTime = 5000
  // 目标源
  protected targetOrigin: string = '*'
  // 消息处理函数
  protected handlers: Map<string, Set<HandlerFunction>> = new Map()
  // 请求处理函数
  protected pendingRequests: Map<string, RequestHandler> = new Map()

  // 处理待响应的请求
  protected handlePendingRequest(data: Message): boolean {
    if (!data.messageId || !this.pendingRequests.has(data.messageId)) {
      return false
    }
    // 获取请求
    const request = this.pendingRequests.get(data.messageId)
    // 删除请求
    this.pendingRequests.delete(data.messageId)
    // 发送响应
    request?.resolve(data.payload)
    return true
  }

  // 处理事件监听
  protected handleEventListeners(
    data: Message,
    EventHandler: (data: Message, result: any) => void
  ): void {
    const handlers = this.handlers.get(data.action)
    if (!handlers) return

    handlers.forEach(async (handler) => {
      try {
        const result = handler(data.payload ?? {}, data)
        if (data.messageId) {
          EventHandler(data, result)
        }
      } catch (error) {
        console.error(`Error handling message ${data.action}:`, error)
      }
    })
  }

  // 生成消息id
  generateMessageId(): string {
    return `msg_${v1()}`
  }

  baseSend(
    action: string,
    payload: ResponseMessage = defaultPayload,
    pluginId: string,
    messageId: string = '',
    iframe?: HTMLIFrameElement
  ) {
    const message: Message = {
      action,
      payload,
      timestamp: Date.now(),
      source: BRIDGE_SOURCE.MAIN.key,
      pluginId,
      messageId,
      sdkVersion: __SDK_VERSION__,
    }
    this.postMessage(message, iframe)
  }

  // 发送请求
  baseRequest<T = any>(
    action: string,
    payload: ResponseMessage = defaultPayload,
    timeout = this.defaultTime,
    messageId: string,
    pluginId: string,
    iframe?: HTMLIFrameElement
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      // 设置超时
      const timer = setTimeout(() => {
        if (this.pendingRequests.has(messageId)) {
          this.pendingRequests.delete(messageId)
          reject(new Error(`Request ${action} timed out after ${timeout}ms`))
        }
      }, timeout)

      // 重写resolve和reject
      this.pendingRequests.set(messageId, {
        resolve: (data: T) => {
          clearTimeout(timer)
          resolve(data)
        },
        reject: (error: Error) => {
          clearTimeout(timer)
          reject(error)
        },
      })

      this.baseSend(action, payload, pluginId, messageId, iframe)
    })
  }

  // 统一postMessage
  postMessage(message: Message, iframe?: HTMLIFrameElement) {
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(message, this.targetOrigin)
      return
    }
    window.parent.postMessage(message, this.targetOrigin)
  }

  // 监听消息
  on(action: string, handler: HandlerFunction): void {
    // 如果类型不存在，则创建一个空集合
    const handlers = this.handlers.get(action) || new Set()
    // 添加处理函数
    handlers.add(handler)
    // 设置处理函数
    this.handlers.set(action, handlers)
  }

  // 移除消息
  off(action: string, handler?: HandlerFunction): void {
    // 如果类型不存在，则不进行任何操作
    if (!this.handlers.has(action)) {
      return
    }
    // 如果处理函数不存在，则删除整个类型
    if (!handler) {
      this.handlers.delete(action)
      return
    }
    // 获取处理函数集合
    const handlers = this.handlers.get(action)
    // 如果处理函数集合存在
    if (handlers) {
      // 删除处理函数
      handlers.delete(handler)
      // 如果处理函数集合为空，则删除整个类型
      if (handlers.size === 0) {
        this.handlers.delete(action)
      }
    }
  }
}

export default Core
